import crypto from 'crypto'

import axios from 'axios'
import qs from 'qs'

import Api from '~/utils/Api'

const calculateChecksum = (file: any): Promise<any> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    const md5sum = crypto.createHash('md5')

    reader.readAsBinaryString(file)

    reader.onload = (e: any): void => {
      md5sum.update(e.target.result, 'binary')
      resolve(md5sum.digest('base64'))
    }

    reader.onerror = (e: any): void => {
      reject(e)
    }
  })

class REF extends Api {
  /**
   * User login
   */
  login = (user: SignInInput): Promise<{ jwt: string }> =>
    this.post('/api/v1/user/sessions', {
      data: user
    })

  /**
   * Update token
   */
  updateToken = (): Promise<{ jwt: string }> => this.post('/api/v1/user/tokens')

  /**
   * Reset password
   */
  resetPassword = (values: ResetPasswordDTO): Promise<{ jwt: string }> =>
    this.post('/api/v1/user/passwords', {
      data: values
    })

  /**
   * Send reset link password
   */
  sendResetLink = (values: ForgotPasswordInput): Promise<void> =>
    this.post('/api/v1/user/passwords/emails', {
      data: values
    })

  /**
   * Get center by id
   */
  getCenter = (id: string): Promise<ResponseObject<User>> =>
    this.get(`/api/v1/image_centers/${id}`)

  /**
   * Update clinic
   */
  updateCenter = (
    id: string,
    image_center: NewClinicInput
  ): Promise<ResponseObject<Clinic>> =>
    this.put(`/api/v1/image_centers/${id}`, { data: { image_center } })

  /**
   * Update profile
   */
  updateProfile = (
    id: string,
    values: EditProfileInput
  ): Promise<ResponseObject<User>> =>
    this.put(`/api/v1/clinics/${id}`, {
      data: {
        id,
        clinic: values
      }
    })

  /**
   * Get clinics
   */
  getClinics = ({
    status,
    ...params
  }: QueryParams): Promise<{ clinics: ResponseObject<Clinic[]>; pagy: Meta }> =>
    this.get('/api/v1/clinics', {
      params: {
        ...params,
        ...(status && { by_status: status })
      },
      paramsSerializer: (params) => qs.stringify(params)
    })

  /**
   * Get all active clinics without pagination
   */
  getActiveClinics = (): Promise<ResponseObject<Clinic[]>> =>
    this.get('/api/v1/clinic_referrals/index')

  /**
   * Get clinic
   */
  getClinicById = (id: string): Promise<ResponseObject<Clinic>> =>
    this.get(`/api/v1/clinics/${id}`)

  /**
   * Create clinic
   */
  createClinic = (clinic: NewClinicInput): Promise<ResponseObject<Clinic>> =>
    this.post(`/api/v1/clinics`, { data: { clinic } })

  /**
   * Update clinic
   */
  updateClinic = (
    id: string,
    clinic: NewClinicInput
  ): Promise<ResponseObject<Clinic>> =>
    this.put(`/api/v1/clinics/${id}`, { data: { clinic } })

  /**
   * Direct upload helper
   */
  _directUpload = async (
    file: any
  ): Promise<{
    signed_id: string
    origin: object
    id: string
    name: string
  }> => {
    try {
      const checksum = await calculateChecksum(file)

      const {
        direct_upload: { url, headers },
        key,
        signed_id,
        filename
      } = await this.post('/rails/active_storage/direct_uploads', {
        data: {
          blob: {
            filename: file.name || 'image',
            byte_size: file.size,
            content_type: file.type,
            checksum
          }
        }
      })

      await axios.put(url, file, {
        headers
      })

      return { signed_id, origin: file, id: key, name: filename }
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Direct upload of one file
   */
  directUploadOne = (
    file: any
  ): Promise<{
    signed_id: string
    origin: object
    id: string
    name: string
  }> => this._directUpload(file)

  /*
   * Get clinics dashboard
   */
  getClinicDashboards = (): Promise<ResponseObject> =>
    this.get('/api/v1/clinics/dashboard')

  /**
   * Get center dashboard
   */
  getCenterDashboards = (): Promise<ResponseObject> =>
    this.get('/api/v1/image_centers/dashboard')

  /**
   * Get referrals
   */
  getReferrals = (
    params: QueryParams
  ): Promise<{ referrals: Referral[]; pagy: Meta }> =>
    this.get(`/api/v1/referrals`, {
      params,
      paramsSerializer: (params) => qs.stringify(params)
    })

  /**
   * Get referral by id
   */
  getReferralById = (id: string): Promise<Referral> =>
    this.get(`/api/v1/referrals/${id}`)

  /**
   * Get public referral by id (with token)
   */
  getPublicReferralById = (token: string): Promise<Referral> =>
    this.get(`/api/v1/public/referrals/${token}`)

  /**
   * Update referral by id
   */
  updateReferralById = (
    id: string,
    referral: Partial<ReferralDTO>
  ): Promise<Referral> =>
    this.put(`/api/v1/referrals/${id}`, { data: { referral } })

  /**
   * Create new referral
   */
  createReferral = (referral: ReferralDTO): Promise<ResponseObject<Referral>> =>
    this.post(`/api/v1/referrals`, { data: { referral } })

  /**
   * Auto login
   */
  loginConfirm = (token: string): Promise<{ jwt: string }> =>
    this.post('/api/v1/user/confirmations', {
      params: { token }
    })

  /**
   * Resend Confirmation Email
   */
  resendConfirmationEmail = (email: string): Promise<void> =>
    this.post(`/api/v1/user/resend_email_confirmations`, { data: { email } })

  /**
   * Get messages
   */
  getNotesByClinicId = (
    noteId: string,
    referralId: string
  ): Promise<Referral> => {
    return this.get(
      `api/v1/comments/?recipient_id=${noteId}&referral_id=${referralId}`
    )
  }

  /**
   * Create messages
   */
  createNote = (comment): Promise<Comment> => {
    return this.post(`api/v1/comments`, { data: { comment } })
  }

  /**
   * Delete messages
   */
  deleteNoteById = (commentId: string): Promise<void> =>
    this.del(`api/v1/comments/${commentId}`)

  /**
   * update messages
   */
  updateNotesById = (
    commentId: string,
    commentStatus: CommentStatus
  ): Promise<Comment> => {
    const payload = { id: commentId, comment: { read: commentStatus } }
    return this.put(`api/v1/comments/${commentId}`, {
      data: payload
    })
  }
}

export default new REF({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*'
  }
})
